import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { toast } from 'sonner';
import type { Space } from '@klareospace/pharos-lib-types';
import { getApiErrorDetail } from '@klareospace/pharos-lib-front-utils';

import type { UpdateSpaceFormInput } from '../schemas/update-space.schema';
import { useUpdateSpace } from './use-update-space';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { UpdateSpaceFormInput };

export interface UseUpdateSpaceFormReturn {
  form: UseFormReturn<UpdateSpaceFormInput>;
  isDirty: boolean;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (data: UpdateSpaceFormInput) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUpdateSpaceForm(space: Space): UseUpdateSpaceFormReturn {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');
  const updateSpace = useUpdateSpace(space.id);

  const schema = useMemo(
    () =>
      z.object({
        code: z.string().trim().min(1, tCommon('validation.codeRequired')),
        description: z.string().trim(),
        name: z.string().trim().min(1, tCommon('validation.nameRequired')),
      }),
    [t],
  );

  const form = useForm<UpdateSpaceFormInput>({
    defaultValues: {
      code: space.code,
      description: space.description ?? '',
      name: space.name,
    },
    mode: 'all',
    resolver: zodResolver(schema),
  });

  // Reset form when space data changes (e.g., after a successful update + refetch)
  const { reset } = form;
  useEffect(() => {
    reset({
      code: space.code,
      description: space.description ?? '',
      name: space.name,
    });
  }, [space, reset]);

  // ─── Submit ───────────────────────────────────────────────────────────────

  function onSubmit(data: UpdateSpaceFormInput): void {
    if (updateSpace.isPending) return;

    updateSpace
      .mutateAsync({
        code: data.code,
        description: data.description || undefined,
        name: data.name,
      })
      .then(
        () => {
          toast.success(t('spaces.settings.updateSuccess'));
        },
        (error: unknown) => {
          const detail = getApiErrorDetail(error);
          if (detail.statusCode === 409) {
            const message = detail.message ?? '';
            if (message.includes('code')) {
              form.setError('code', { message: tCommon('validation.codeExists') });
            } else {
              form.setError('name', { message: tCommon('validation.nameExists') });
            }
            return;
          }
          toast.error(detail.message || t('spaces.settings.updateError'));
        },
      );
  }

  function onCancel(): void {
    form.reset();
  }

  return {
    form,
    isDirty: form.formState.isDirty,
    isPending: updateSpace.isPending,
    onCancel,
    onSubmit,
  };
}
